import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, Grid, Chip, Divider, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Stack,
  LinearProgress, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Report } from './Report';
import { ReportPDFGenerator } from './reportPDFGenerator';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';

// ── Shared chart palette (brand-neutral, legible in light & dark) ────────────
const CHART_COLORS = ['#4db6ac', '#5c6bc0', '#ffb74d', '#ba68c8', '#4fc3f7', '#e57373', '#81c784', '#f06292', '#9575cd', '#a1887f'];

const compactNum = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
};

interface Props {
  open: boolean;
  report: Report;
  onClose: () => void;
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
//
// In light mode: soft pastel tints (low-opacity fills).
// In dark mode:  richer semi-transparent fills + brighter text so the chip
//                is legible against dark paper backgrounds.
//
// Pass `isDark` (from useTheme) to switch between the two palettes.
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
  };

  // Dark mode: higher-opacity fills + light/vivid text so chips pop on dark surfaces
  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// ── Map an order status string → chip colour key ─────────────────────────────
const getOrderStatusColor = (status: string): string => {
  const s = status.toLowerCase();
  if (s === 'delivered')                                             return 'success';
  if (s === 'shipped' || s === 'confirmed')                         return 'info';
  if (s === 'processing')                                           return 'primary';
  if (s === 'pending')                                              return 'warning';
  if (s === 'cancelled' || s === 'returned' || s === 'refunded')   return 'error';
  return 'default';
};

// ── Number formatting helpers ────────────────────────────────────────────────
const fmt = (n: number, sym: string) =>
  `${sym} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtNum = (n: number) =>
  n.toLocaleString('en-US');

// ── Summary card ─────────────────────────────────────────────────────────────
// Uses `cardBg` (background.default) so cards appear visually distinct
// from the dialog surface (background.paper) — matching the supplier drawer
// pattern where drawerBg = background.default and cardBg = background.paper.
// In the report dialog the surface is background.paper, so cards use
// background.default to achieve the same contrast relationship.
// ─────────────────────────────────────────────────────────────────────────────
interface SummaryCardProps {
  label: string;
  value: React.ReactNode;
  cardBg: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, cardBg }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      bgcolor: cardBg,
      backgroundImage: 'none',
      borderRadius: 1.5,
      height: '100%',
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
      {value}
    </Typography>
  </Paper>
);

// ── Chart wrapper card ───────────────────────────────────────────────────────
interface ChartCardProps {
  title: string;
  cardBg: string;
  children: React.ReactNode;
  height?: number;
}
const ChartCard: React.FC<ChartCardProps> = ({ title, cardBg, children, height = 280 }) => (
  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5, height: '100%' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>{title}</Typography>
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children as any}
      </ResponsiveContainer>
    </Box>
  </Paper>
);

// Reusable grouped/simple bar chart. `bars` = series to plot against `xKey`.
//
// NOTE: ResponsiveContainer works by cloning its *immediate* child and
// injecting numeric `width`/`height`/`style` props onto it (see recharts'
// ResponsiveContainer.js). Since these wrapper components sit between
// ResponsiveContainer and the actual <BarChart>/<PieChart>, they must accept
// and forward those injected props — otherwise the real chart never
// receives a size and silently renders nothing.
interface BarSpec { key: string; name: string; color?: string; }
const ReportBarChart: React.FC<{
  data: any[]; xKey: string; bars: BarSpec[]; gridColor: string; axisColor: string; sym: string; currency?: boolean;
  width?: number; height?: number; style?: React.CSSProperties;
}> = ({ data, xKey, bars, gridColor, axisColor, sym, currency, width, height, style }) => (
  <BarChart data={data} width={width} height={height} style={style} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
    <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: axisColor }} interval="preserveStartEnd" />
    <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={compactNum} width={48} />
    <Tooltip formatter={(v: any) => (currency ? `${sym} ${Number(v).toLocaleString()}` : Number(v).toLocaleString())} />
    {bars.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
    {bars.map((b, i) => (
      <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color || CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
    ))}
  </BarChart>
);

const ReportPieChart: React.FC<{
  data: any[]; nameKey: string; valueKey: string; sym: string;
  width?: number; height?: number; style?: React.CSSProperties;
}> = ({ data, nameKey, valueKey, sym, width, height, style }) => (
  <PieChart width={width} height={height} style={style}>
    <Pie data={data} dataKey={valueKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
      {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
    </Pie>
    <Tooltip formatter={(v: any) => `${sym} ${Number(v).toLocaleString()}`} />
    <Legend wrapperStyle={{ fontSize: 11 }} />
  </PieChart>
);

// ─────────────────────────────────────────────────────────────────────────────

const ReportDetailsDialog: React.FC<Props> = ({ open, report, onClose }) => {
  const { companyProfile } = useCompany();
  const companyCurrency = companyProfile?.currency || 'LKR';
  const sym             = getCurrencySymbol(companyCurrency);

  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ── Colors sourced entirely from the MUI theme (set via themeConfig.ts) ──
  // dialogBg → background.paper   (the dialog surface)
  // cardBg   → background.default (cards sit on top — darker bg gives contrast)
  //
  // This mirrors the supplier drawer pattern:
  //   drawerBg = background.default  (shell/outer)
  //   cardBg   = background.paper    (cards inside)
  // Here the dialog IS background.paper, so we invert:
  //   dialogBg = background.paper
  //   cardBg   = background.default  (slightly darker → visible card contrast)
  // ─────────────────────────────────────────────────────────────────────────
  const dialogBg = theme.palette.background.default;
  const cardBg   = theme.palette.background.paper;

  // Chart axis / grid colours derived from theme for light+dark legibility.
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const axisColor = theme.palette.text.secondary;

  const handleDownloadPDF = async () => {
    try { await ReportPDFGenerator.generatePDF(report, companyCurrency); }
    catch { alert('Failed to download PDF'); }
  };

  // ── Sales ──────────────────────────────────────────────────────────────────
  const renderSalesReport = () => {
    const data = report.data;
    if (!data.orders) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Orders"    value={fmtNum(data.summary.totalOrders)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Revenue"   value={fmt(data.summary.totalRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Avg Order Value" value={fmt(data.summary.averageOrderValue, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Tax"       value={fmt(data.summary.totalTax, sym)} /></Grid>
        </Grid>

        {data.summary.topProducts?.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <ChartCard title="Top Products by Revenue" cardBg={cardBg}>
                <ReportBarChart
                  data={data.summary.topProducts.slice(0, 8).map((p: any) => ({ name: p.productName, revenue: p.revenue }))}
                  xKey="name" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                  bars={[{ key: 'revenue', name: 'Revenue', color: CHART_COLORS[0] }]}
                />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={5}>
              <ChartCard title="Top Products Share" cardBg={cardBg}>
                <ReportPieChart data={data.summary.topProducts.slice(0, 6).map((p: any) => ({ name: p.productName, revenue: p.revenue }))} nameKey="name" valueKey="revenue" sym={sym} />
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {data.summary.topProducts?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Products</Typography>
            <Grid container spacing={2}>
              {data.summary.topProducts.slice(0, 5).map((p: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>{p.productName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Quantity: {fmtNum(p.quantity)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Revenue: {fmt(p.revenue, sym)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Orders</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell><TableCell>Customer</TableCell><TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell><TableCell>Payment</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.slice(0, 50).map((o: any) => (
                <TableRow key={o.orderId}>
                  <TableCell>{o.orderNumber}</TableCell>
                  <TableCell>{o.customerName}</TableCell>
                  <TableCell>{o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell align="right">{fmt(o.totalAmount, sym)}</TableCell>
                  <TableCell><Chip label={o.paymentMethod} size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} /></TableCell>
                  <TableCell><Chip label={o.status}        size="small" variant="outlined" sx={getSoftChipSx(getOrderStatusColor(o.status), isDark)} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Inventory ──────────────────────────────────────────────────────────────
  const renderInventoryReport = () => {
    const data = report.data;
    if (!data.products) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Products" value={fmtNum(data.summary.totalProducts)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Stock"    value={fmtNum(data.summary.totalStock)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Stock Value"    value={fmt(data.summary.totalValue, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Low Stock"      value={fmtNum(data.summary.lowStockItems)} /></Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>Products</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>SKU</TableCell><TableCell>Category</TableCell>
                <TableCell align="right">Stock</TableCell><TableCell align="right">Value</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.products.slice(0, 50).map((p: any) => (
                <TableRow key={p.productId}>
                  <TableCell>
                    {p.productName}
                    {p.hasVariants && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {p.variants?.length || 0} variants
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{p.sku}</TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell align="right">{fmtNum(p.totalStock)}</TableCell>
                  <TableCell align="right">{fmt(p.stockValue, sym)}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.status.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(
                        p.status === 'in_stock' ? 'success' : p.status === 'low_stock' ? 'warning' : 'error',
                        isDark
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Purchase Orders ────────────────────────────────────────────────────────
  const renderPurchaseOrderReport = () => {
    const data = report.data;
    if (!data.purchaseOrders || data.purchaseOrders.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No Purchase Orders Found</Typography>
          <Typography variant="body2" color="text.secondary">No purchase orders match the selected criteria.</Typography>
        </Box>
      );
    }
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total POs"    value={fmtNum(data.summary.totalPOs)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Amount" value={fmt(data.summary.totalAmount || 0, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Paid"         value={fmt(data.summary.totalPaid || 0, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Pending"      value={fmt((data.summary.totalAmount || 0) - (data.summary.totalPaid || 0), sym)} /></Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0 && (
            <Grid item xs={12} md={6}>
              <ChartCard title="POs by Status" cardBg={cardBg}>
                <ReportPieChart
                  data={Object.entries(data.summary.statusBreakdown).map(([name, value]) => ({ name, value: value as number }))}
                  nameKey="name" valueKey="value" sym={sym}
                />
              </ChartCard>
            </Grid>
          )}
          {data.summary.topSuppliers?.length > 0 && (
            <Grid item xs={12} md={6}>
              <ChartCard title="Top Suppliers by Spend" cardBg={cardBg}>
                <ReportBarChart
                  data={data.summary.topSuppliers.slice(0, 8).map((s: any) => ({ name: s.supplierName, amount: s.totalAmount || s.totalPurchased || 0 }))}
                  xKey="name" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                  bars={[{ key: 'amount', name: 'Amount', color: CHART_COLORS[2] }]}
                />
              </ChartCard>
            </Grid>
          )}
        </Grid>

        {data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Status Breakdown</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {Object.entries(data.summary.statusBreakdown).map(([status, count]) => (
                <Chip key={status} label={`${status}: ${count}`} size="small" variant="outlined" sx={getSoftChipSx('default', isDark)} />
              ))}
            </Stack>
          </Box>
        )}

        {data.summary.paymentStatusBreakdown && Object.keys(data.summary.paymentStatusBreakdown).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Payment Status</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {Object.entries(data.summary.paymentStatusBreakdown).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${status}: ${count}`}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(status === 'Paid' ? 'success' : status === 'Partial' ? 'warning' : 'default', isDark)}
                />
              ))}
            </Stack>
          </Box>
        )}

        {data.summary.topSuppliers?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Suppliers</Typography>
            <Grid container spacing={2}>
              {data.summary.topSuppliers.slice(0, 5).map((s: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>{s.supplierName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Orders: {s.poCount}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Total: {fmt(s.totalAmount, sym)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Purchase Orders</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>PO #</TableCell><TableCell>Supplier</TableCell><TableCell>Order Date</TableCell>
                <TableCell>Exp. Delivery</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Payment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.purchaseOrders.slice(0, 50).map((po: any) => (
                <TableRow key={po.poId}>
                  <TableCell><Typography variant="body2" fontWeight="medium">{po.poNumber}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{po.supplierName}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{po.orderDate instanceof Date ? po.orderDate.toLocaleDateString() : 'N/A'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{po.expectedDelivery instanceof Date ? po.expectedDelivery.toLocaleDateString() : 'Not set'}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">{fmt(po.totalAmount || 0, sym)}</Typography>
                    {po.paidAmount > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Paid: {fmt(po.paidAmount, sym)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={po.status || 'draft'}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(po.status === 'Received' ? 'success' : po.status === 'Approved' ? 'info' : 'warning', isDark)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={po.paymentStatus || 'pending'}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(po.paymentStatus === 'Paid' ? 'success' : po.paymentStatus === 'Partial' ? 'warning' : 'default', isDark)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.purchaseOrders.length > 50 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing first 50 of {data.purchaseOrders.length} purchase orders
          </Typography>
        )}
      </Box>
    );
  };

  // ── Low Stock ──────────────────────────────────────────────────────────────
  const renderLowStockReport = () => {
    const data = report.data;
    if (!data.items) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Low Stock Items"     value={fmtNum(data.summary.totalLowStockItems)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Out of Stock"        value={fmtNum(data.summary.totalOutOfStockItems)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Affected Products"   value={fmtNum(data.summary.affectedProducts)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Affected Warehouses" value={fmtNum(data.summary.affectedWarehouses)} /></Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>Low Stock Items</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>Warehouse</TableCell>
                <TableCell align="right">Current</TableCell><TableCell align="right">Min</TableCell>
                <TableCell align="right">Deficit</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((item: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>
                    {item.productName}
                    {item.variantName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.variantName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.warehouseName}</TableCell>
                  <TableCell align="right">{fmtNum(item.currentStock)}</TableCell>
                  <TableCell align="right">{fmtNum(item.minStock)}</TableCell>
                  <TableCell align="right">{fmtNum(item.deficit)}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock'}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(item.status === 'out_of_stock' ? 'error' : 'warning', isDark)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Warehouse Performance ──────────────────────────────────────────────────
  const renderWarehousePerformanceReport = () => {
    const data = report.data;
    if (!data.warehouses) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Warehouses" value={fmtNum(data.summary.totalWarehouses)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Avg Utilization"  value={`${data.summary.averageUtilization}%`} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Most Utilized"    value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.mostUtilizedWarehouse}</Typography>} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Least Utilized"   value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.leastUtilizedWarehouse}</Typography>} /></Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>Warehouses</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Warehouse</TableCell><TableCell>Code</TableCell>
                <TableCell align="right">Products</TableCell><TableCell align="right">Stock</TableCell>
                <TableCell align="right">Capacity Used</TableCell><TableCell align="right">Utilization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.warehouses.map((w: any) => (
                <TableRow key={w.warehouseId}>
                  <TableCell>
                    {w.warehouseName}
                    {w.manager && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Manager: {w.manager}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{w.code}</TableCell>
                  <TableCell align="right">{fmtNum(w.totalProducts)}</TableCell>
                  <TableCell align="right">{fmtNum(w.totalStock)}</TableCell>
                  <TableCell align="right">{fmtNum(w.capacityUsed)}/{fmtNum(w.capacityTotal)}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${w.utilizationRate}%`}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(w.utilizationRate > 80 ? 'error' : w.utilizationRate > 60 ? 'warning' : 'success', isDark)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Product Performance ────────────────────────────────────────────────────
  const renderProductPerformanceReport = () => {
    const data = report.data;
    if (!data.products) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Products" value={fmtNum(data.summary.totalProducts)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Total Revenue"  value={fmt(data.summary.totalRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Best Seller"    value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.bestSellingProduct}</Typography>} /></Grid>
          <Grid item xs={6} sm={3}><SummaryCard cardBg={cardBg} label="Avg Turnover"   value={data.summary.averageStockTurnover} /></Grid>
        </Grid>

        {data.products?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ChartCard title="Top Products by Revenue" cardBg={cardBg}>
              <ReportBarChart
                data={[...data.products].sort((a: any, b: any) => (b.revenue || 0) - (a.revenue || 0)).slice(0, 10).map((p: any) => ({ name: p.productName, revenue: p.revenue, sold: p.totalSold }))}
                xKey="name" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                bars={[{ key: 'revenue', name: 'Revenue', color: CHART_COLORS[0] }]}
              />
            </ChartCard>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Products</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell><TableCell>Category</TableCell>
                <TableCell align="right">Sold</TableCell><TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Avg Price</TableCell><TableCell align="right">Turnover</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.products.slice(0, 50).map((p: any) => (
                <TableRow key={p.productId}>
                  <TableCell>
                    {p.productName}
                    {p.isFeatured && (
                      <Chip
                        label="Featured"
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, ...getSoftChipSx('primary', isDark) }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell align="right">{fmtNum(p.totalSold)}</TableCell>
                  <TableCell align="right">{fmt(p.revenue, sym)}</TableCell>
                  <TableCell align="right">{fmt(p.averagePrice, sym)}</TableCell>
                  <TableCell align="right">{p.stockTurnover}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Revenue ────────────────────────────────────────────────────────────────
  const renderRevenueReport = () => {
    const data = report.data;
    if (!data.dailyRevenue) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Total Revenue"     value={fmt(data.summary.totalRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Net Revenue"       value={fmt(data.summary.totalNetRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Total Orders"      value={fmtNum(data.summary.totalOrders)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Avg Order Value"   value={fmt(data.summary.averageOrderValue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Avg Daily Revenue" value={fmt(data.summary.averageDailyRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <SummaryCard
              cardBg={cardBg}
              label="Growth Rate"
              value={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ color: data.summary.growthRate >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                    {data.summary.growthRate >= 0 ? '+' : ''}{data.summary.growthRate.toFixed(2)}%
                  </Typography>
                  {data.summary.growthRate >= 0
                    ? <TrendingUpIcon color="success" fontSize="small" />
                    : <TrendingDownIcon color="error" fontSize="small" />}
                </Stack>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Highest Revenue Day" value={<Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>{data.summary.highestRevenueDay}</Typography>} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Total Discount"      value={fmt(data.summary.totalDiscount, sym)} /></Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {data.dailyRevenue?.length > 0 && (
            <Grid item xs={12} md={data.topCategories?.length > 0 ? 8 : 12}>
              <ChartCard title="Daily Revenue Trend" cardBg={cardBg}>
                <LineChart
                  data={data.dailyRevenue.map((d: any) => ({
                    date: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                    revenue: d.totalRevenue,
                    net: d.netRevenue,
                  }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: axisColor }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={compactNum} width={48} />
                  <Tooltip formatter={(v: any) => `${sym} ${Number(v).toLocaleString()}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="revenue" name="Revenue" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="net" name="Net Revenue" stroke={CHART_COLORS[1]} strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Grid>
          )}
          {data.topCategories?.length > 0 && (
            <Grid item xs={12} md={4}>
              <ChartCard title="Revenue by Category" cardBg={cardBg}>
                <ReportPieChart data={data.topCategories.slice(0, 8)} nameKey="categoryName" valueKey="revenue" sym={sym} />
              </ChartCard>
            </Grid>
          )}
        </Grid>

        {data.topProducts?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Products by Revenue</Typography>
            <Grid container spacing={2}>
              {data.topProducts.slice(0, 5).map((p: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{p.productName}</Typography>
                      <Chip
                        label={`${p.percentageOfTotal.toFixed(1)}%`}
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx('primary', isDark)}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">Revenue: {fmt(p.revenue, sym)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Units Sold: {fmtNum(p.totalSold)}</Typography>
                    <LinearProgress variant="determinate" value={p.percentageOfTotal} sx={{ mt: 1, height: 6, borderRadius: 1 }} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {data.topCategories?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Categories by Revenue</Typography>
            <Grid container spacing={2}>
              {data.topCategories.slice(0, 5).map((c: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{c.categoryName}</Typography>
                      <Chip
                        label={`${c.percentageOfTotal.toFixed(1)}%`}
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx('secondary', isDark)}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">Revenue: {fmt(c.revenue, sym)}</Typography>
                    <LinearProgress variant="determinate" value={c.percentageOfTotal} color="secondary" sx={{ mt: 1, height: 6, borderRadius: 1 }} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Daily Revenue</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell align="right">Orders</TableCell>
                <TableCell align="right">Revenue</TableCell><TableCell align="right">Net Revenue</TableCell>
                <TableCell align="right">Avg Order</TableCell><TableCell align="right">Tax</TableCell><TableCell align="right">Shipping</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.dailyRevenue.map((day: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{day.date instanceof Date ? day.date.toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell align="right">{fmtNum(day.totalOrders)}</TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="medium">{fmt(day.totalRevenue, sym)}</Typography></TableCell>
                  <TableCell align="right">{fmt(day.netRevenue, sym)}</TableCell>
                  <TableCell align="right">{fmt(day.averageOrderValue, sym)}</TableCell>
                  <TableCell align="right">{fmt(day.totalTax, sym)}</TableCell>
                  <TableCell align="right">{fmt(day.totalShipping, sym)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── Returns & Cancellations ────────────────────────────────────────────────
  const renderReturnsCancellationsReport = () => {
    const data = report.data;
    if (!data.orders) return null;
    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}><SummaryCard cardBg={cardBg} label="Total Returned"  value={fmtNum(data.summary.totalReturned)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard cardBg={cardBg} label="Total Cancelled" value={fmtNum(data.summary.totalCancelled)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard cardBg={cardBg} label="Total Refunded"  value={fmtNum(data.summary.totalRefunded)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard cardBg={cardBg} label="Total Orders"    value={fmtNum(data.summary.totalOrders)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><SummaryCard cardBg={cardBg} label="Lost Revenue"    value={fmt(data.summary.totalLostRevenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: cardBg,
                backgroundImage: 'none',
                borderRadius: 1.5,
                height: '100%',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Rates</Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>Return: {data.summary.returnRate}%</Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>Cancel: {data.summary.cancellationRate}%</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Returned vs Cancelled vs Refunded" cardBg={cardBg} height={240}>
              <ReportBarChart
                data={[
                  { name: 'Returned', value: data.summary.totalReturned },
                  { name: 'Cancelled', value: data.summary.totalCancelled },
                  { name: 'Refunded', value: data.summary.totalRefunded },
                ]}
                xKey="name" gridColor={gridColor} axisColor={axisColor} sym={sym}
                bars={[{ key: 'value', name: 'Orders', color: CHART_COLORS[5] }]}
              />
            </ChartCard>
          </Grid>
          {data.summary.topReturnedProducts?.length > 0 && (
            <Grid item xs={12} md={6}>
              <ChartCard title="Top Returned Products (Lost Revenue)" cardBg={cardBg} height={240}>
                <ReportBarChart
                  data={data.summary.topReturnedProducts.slice(0, 6).map((p: any) => ({ name: p.productName, revenue: p.revenue }))}
                  xKey="name" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                  bars={[{ key: 'revenue', name: 'Lost Revenue', color: CHART_COLORS[3] }]}
                />
              </ChartCard>
            </Grid>
          )}
        </Grid>

        {data.summary.topReturnedProducts?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Top Returned Products</Typography>
            <Grid container spacing={2}>
              {data.summary.topReturnedProducts.slice(0, 5).map((p: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>{p.productName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Quantity: {fmtNum(p.quantity)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Lost Revenue: {fmt(p.revenue, sym)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Orders</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell><TableCell>Customer</TableCell><TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell><TableCell>Status</TableCell>
                <TableCell>Payment</TableCell><TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.slice(0, 50).map((o: any) => (
                <TableRow key={o.orderId}>
                  <TableCell>{o.orderNumber}</TableCell>
                  <TableCell>{o.customerName}</TableCell>
                  <TableCell>{o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell align="right">{fmt(o.totalAmount, sym)}</TableCell>
                  <TableCell>
                    <Chip
                      label={o.status}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(o.status === 'Returned' ? 'warning' : o.status === 'Cancelled' ? 'error' : 'secondary', isDark)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={o.paymentMethod} size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{o.reason || '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.orders.length > 50 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing first 50 of {data.orders.length} orders
          </Typography>
        )}
      </Box>
    );
  };

  // ── Profit & Loss ──────────────────────────────────────────────────────────
  const renderProfitLossReport = () => {
    const data = report.data;
    if (!data.summary) return null;
    const s = data.summary;
    const netPositive = s.netProfit >= 0;
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Period: {s.periodDays} day{s.periodDays === 1 ? '' : 's'} · {fmtNum(s.totalOrders)} delivered order{s.totalOrders === 1 ? '' : 's'}.
          Cost of goods sold uses the weighted-average cost snapshotted at the moment each sale deducted stock — orders
          placed before that tracking existed may show $0 cost.
        </Typography>

        <Typography variant="h6" gutterBottom>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Revenue" value={fmt(s.revenue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="COGS" value={fmt(s.cogs, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Gross Profit" value={fmt(s.grossProfit, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Gross Margin" value={`${s.grossMarginPercent.toFixed(1)}%`} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Operating Costs" value={fmt(s.operatingCosts, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="  — One-Time" value={fmt(s.operatingCostsOneTime, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="  — Recurring (prorated)" value={fmt(s.operatingCostsRecurring, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <SummaryCard
              cardBg={cardBg}
              label="Net Profit"
              value={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ color: netPositive ? 'success.main' : 'error.main', fontWeight: 700 }}>
                    {fmt(s.netProfit, sym)}
                  </Typography>
                  {netPositive
                    ? <TrendingUpIcon color="success" fontSize="small" />
                    : <TrendingDownIcon color="error" fontSize="small" />}
                </Stack>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Net Margin" value={`${s.netMarginPercent.toFixed(1)}%`} /></Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={data.costBreakdown?.length > 0 ? 7 : 12}>
            <ChartCard title="Revenue → Profit Waterfall" cardBg={cardBg}>
              <ReportBarChart
                data={[
                  { name: 'Revenue', value: s.revenue },
                  { name: 'COGS', value: s.cogs },
                  { name: 'Gross Profit', value: s.grossProfit },
                  { name: 'Op. Costs', value: s.operatingCosts },
                  { name: 'Net Profit', value: s.netProfit },
                ]}
                xKey="name" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                bars={[{ key: 'value', name: 'Amount', color: CHART_COLORS[1] }]}
              />
            </ChartCard>
          </Grid>
          {data.costBreakdown?.length > 0 && (
            <Grid item xs={12} md={5}>
              <ChartCard title="Operating Costs by Category" cardBg={cardBg}>
                <ReportPieChart data={data.costBreakdown} nameKey="category" valueKey="amount" sym={sym} />
              </ChartCard>
            </Grid>
          )}
        </Grid>

        {data.costBreakdown?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Operating Costs by Category</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">One-Time</TableCell>
                    <TableCell align="right">Recurring</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Entries</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.costBreakdown.map((c: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{c.category}</TableCell>
                      <TableCell align="right">{fmt(c.oneTimeAmount, sym)}</TableCell>
                      <TableCell align="right">{fmt(c.recurringAmount, sym)}</TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="medium">{fmt(c.amount, sym)}</Typography></TableCell>
                      <TableCell align="right">{fmtNum(c.count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Revenue &amp; Profit by Product</Typography>
        {data.revenueByProduct?.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Qty Sold</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">COGS</TableCell>
                  <TableCell align="right">Gross Profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.revenueByProduct.slice(0, 50).map((p: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{p.productName}</TableCell>
                    <TableCell align="right">{fmtNum(p.quantitySold)}</TableCell>
                    <TableCell align="right">{fmt(p.revenue, sym)}</TableCell>
                    <TableCell align="right">{fmt(p.cogs, sym)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" sx={{ color: p.grossProfit >= 0 ? 'success.main' : 'error.main' }}>
                        {fmt(p.grossProfit, sym)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">No delivered orders in this date range.</Typography>
        )}
        {data.revenueByProduct?.length > 50 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing first 50 of {data.revenueByProduct.length} products
          </Typography>
        )}
      </Box>
    );
  };

  // ── Cost Report ──────────────────────────────────────────────────────────
  const renderCostReport = () => {
    const data = report.data;
    if (!data.summary) return null;
    const s = data.summary;
    const timeline = (data.costTimeline || []).map((t: any) => ({ ...t }));
    const typeBreakdown = (data.costTypeBreakdown || []).filter((x: any) => x.amount > 0);
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Consolidated cost across operating costs (prorated), purchase orders / imports, and COGS over {s.periodDays} day{s.periodDays === 1 ? '' : 's'}.
        </Typography>

        <Typography variant="h6" gutterBottom>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Total Cost (Op + Purchases)" value={fmt(s.totalCost, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Operating Costs" value={fmt(s.operatingCosts, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Purchases / Imports" value={fmt(s.purchaseCosts, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="COGS (goods sold)" value={fmt(s.cogs, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Purchases Paid" value={fmt(s.purchaseCostsPaid, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Purchases Outstanding" value={fmt(s.purchaseCostsOutstanding, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Top Cost Category" value={s.topCostCategory} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Top Supplier" value={s.topCostSupplier} /></Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {timeline.length > 0 && (
            <Grid item xs={12} md={7}>
              <ChartCard title="Cost Trend by Month" cardBg={cardBg}>
                <ReportBarChart
                  data={timeline} xKey="period" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                  bars={[
                    { key: 'operatingCosts', name: 'Operating', color: CHART_COLORS[1] },
                    { key: 'purchaseCosts', name: 'Purchases', color: CHART_COLORS[2] },
                  ]}
                />
              </ChartCard>
            </Grid>
          )}
          {typeBreakdown.length > 0 && (
            <Grid item xs={12} md={5}>
              <ChartCard title="Cost Composition" cardBg={cardBg}>
                <ReportPieChart data={typeBreakdown} nameKey="label" valueKey="amount" sym={sym} />
              </ChartCard>
            </Grid>
          )}
          {data.costByCategory?.length > 0 && (
            <Grid item xs={12} md={6}>
              <ChartCard title="Operating Cost by Category" cardBg={cardBg}>
                <ReportBarChart
                  data={data.costByCategory.slice(0, 8)} xKey="category" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                  bars={[{ key: 'amount', name: 'Amount', color: CHART_COLORS[0] }]}
                />
              </ChartCard>
            </Grid>
          )}
          {data.costBySupplier?.length > 0 && (
            <Grid item xs={12} md={6}>
              <ChartCard title="Purchase Spend by Supplier" cardBg={cardBg}>
                <ReportBarChart
                  data={data.costBySupplier.slice(0, 8)} xKey="supplierName" gridColor={gridColor} axisColor={axisColor} sym={sym} currency
                  bars={[
                    { key: 'totalPurchased', name: 'Purchased', color: CHART_COLORS[3] },
                    { key: 'outstanding', name: 'Outstanding', color: CHART_COLORS[5] },
                  ]}
                />
              </ChartCard>
            </Grid>
          )}
        </Grid>

        {data.costByCategory?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Operating Costs by Category</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">One-Time</TableCell>
                    <TableCell align="right">Recurring</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">% of Operating</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.costByCategory.map((c: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{c.category}</TableCell>
                      <TableCell align="right">{fmt(c.oneTimeAmount, sym)}</TableCell>
                      <TableCell align="right">{fmt(c.recurringAmount, sym)}</TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="medium">{fmt(c.amount, sym)}</Typography></TableCell>
                      <TableCell align="right">{c.percentageOfOperating.toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {data.costBySupplier?.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Purchases / Imports by Supplier</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">POs</TableCell>
                    <TableCell align="right">Purchased</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.costBySupplier.map((c: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{c.supplierName}</TableCell>
                      <TableCell align="right">{fmtNum(c.poCount)}</TableCell>
                      <TableCell align="right">{fmt(c.totalPurchased, sym)}</TableCell>
                      <TableCell align="right">{fmt(c.totalPaid, sym)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium" sx={{ color: c.outstanding > 0 ? 'error.main' : 'success.main' }}>
                          {fmt(c.outstanding, sym)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    );
  };

  // ── Payments & Arrears Report ──────────────────────────────────────────────
  const renderPaymentsReport = () => {
    const data = report.data;
    if (!data.summary) return null;
    const s = data.summary;
    const netPositive = s.netCashPosition >= 0;
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Money received from customers vs. paid to suppliers, plus outstanding customer dues and supplier arrears.
        </Typography>

        <Typography variant="h6" gutterBottom>Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Received (Customers)" value={fmt(s.totalReceived, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Customer Dues" value={fmt(s.totalCustomerDue, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Paid to Suppliers" value={fmt(s.totalPaidToSuppliers, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Supplier Arrears" value={fmt(s.totalSupplierArrears, sym)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Due Orders" value={fmtNum(s.customerDueCount)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Arrear POs" value={fmtNum(s.supplierArrearsCount)} /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <SummaryCard cardBg={cardBg} label="Net Cash Position" value={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography sx={{ color: netPositive ? 'success.main' : 'error.main', fontWeight: 700 }}>{fmt(s.netCashPosition, sym)}</Typography>
                {netPositive ? <TrendingUpIcon color="success" fontSize="small" /> : <TrendingDownIcon color="error" fontSize="small" />}
              </Stack>
            } />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {data.paymentTimeline?.length > 0 && (
            <Grid item xs={12} md={7}>
              <ChartCard title="Received vs. Paid Out by Month" cardBg={cardBg}>
                <AreaChart data={data.paymentTimeline} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: axisColor }} />
                  <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={compactNum} width={48} />
                  <Tooltip formatter={(v: any) => `${sym} ${Number(v).toLocaleString()}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="received" name="Received" stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.25} />
                  <Area type="monotone" dataKey="paidOut" name="Paid Out" stroke={CHART_COLORS[5]} fill={CHART_COLORS[5]} fillOpacity={0.2} />
                </AreaChart>
              </ChartCard>
            </Grid>
          )}
          {data.incomingByMethod?.length > 0 && (
            <Grid item xs={12} md={5}>
              <ChartCard title="Payments Received by Method" cardBg={cardBg}>
                <ReportPieChart data={data.incomingByMethod} nameKey="method" valueKey="amount" sym={sym} />
              </ChartCard>
            </Grid>
          )}
        </Grid>

        {data.supplierArrears?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Supplier Arrears (Outstanding Payables)</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>PO #</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.supplierArrears.slice(0, 50).map((a: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{a.poNumber}</TableCell>
                      <TableCell>{a.supplierName}</TableCell>
                      <TableCell align="right">{fmt(a.totalAmount, sym)}</TableCell>
                      <TableCell align="right">{fmt(a.paidAmount, sym)}</TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="medium" sx={{ color: 'error.main' }}>{fmt(a.outstanding, sym)}</Typography></TableCell>
                      <TableCell><Chip label={a.status} size="small" variant="outlined" sx={getSoftChipSx('warning', isDark)} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {data.customerDues?.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Customer Dues (Unpaid / Partial Orders)</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Order #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Due</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.customerDues.slice(0, 50).map((d: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{d.orderNumber}</TableCell>
                      <TableCell>{d.customerName}</TableCell>
                      <TableCell align="right">{fmt(d.totalAmount, sym)}</TableCell>
                      <TableCell align="right">{fmt(d.paidAmount, sym)}</TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight="medium" sx={{ color: 'warning.main' }}>{fmt(d.due, sym)}</Typography></TableCell>
                      <TableCell><Chip label={d.paymentStatus} size="small" variant="outlined" sx={getSoftChipSx('info', isDark)} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    );
  };

  // ── AI Forecast ────────────────────────────────────────────────────────────
  const forecastMetricLabel = (t: string) => (t === 'revenue' ? 'Revenue' : t === 'sales' ? 'Orders' : 'Units Sold');

  const renderForecastReport = () => {
    const data = report.data;
    if (!data?.predictions) return null;
    const isCurrency = data.forecastType === 'revenue';
    const fmtVal = (n: number) => (isCurrency ? fmt(n, sym) : fmtNum(Math.round(n)));

    const historicalRows = (data.historicalData?.dataPoints || []).map((p: any) => ({
      date: p.date instanceof Date ? p.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : String(p.date),
      actual: p.value,
    }));
    const predictedRows = (data.predictions || []).map((p: any) => ({
      date: p.date instanceof Date ? p.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : String(p.date),
      predicted: p.predictedValue,
      lowerBound: p.lowerBound,
      upperBound: p.upperBound,
    }));
    const chartData = [...historicalRows, ...predictedRows];

    const confidenceColor = data.confidence?.level === 'high' ? 'success' : data.confidence?.level === 'low' ? 'error' : 'warning';

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Forecast Summary</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Forecasting" value={forecastMetricLabel(data.forecastType)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Forecast Period" value={`${data.forecastPeriod} days`} /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <SummaryCard
              cardBg={cardBg}
              label="Confidence"
              value={
                <Chip
                  label={`${(data.confidence?.level || 'medium').toUpperCase()} (${data.confidence?.score ?? '—'})`}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(confidenceColor, isDark)}
                />
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <SummaryCard
              cardBg={cardBg}
              label="Expected Trend"
              value={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ color: (data.summary?.expectedGrowth ?? 0) >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                    {(data.summary?.expectedGrowth ?? 0) >= 0 ? '+' : ''}{(data.summary?.expectedGrowth ?? 0).toFixed(2)}%
                  </Typography>
                  {(data.summary?.expectedGrowth ?? 0) >= 0
                    ? <TrendingUpIcon color="success" fontSize="small" />
                    : <TrendingDownIcon color="error" fontSize="small" />}
                </Stack>
              }
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Avg Predicted / Day" value={fmtVal(data.summary?.averagePredictedValue || 0)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Total Predicted" value={fmtVal(data.summary?.totalPredicted || 0)} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Peak Day" value={<Typography variant="body2" sx={{ fontWeight: 700 }}>{data.summary?.peakDate} — {fmtVal(data.summary?.peakValue || 0)}</Typography>} /></Grid>
          <Grid item xs={6} sm={4} md={3}><SummaryCard cardBg={cardBg} label="Lowest Day" value={<Typography variant="body2" sx={{ fontWeight: 700 }}>{data.summary?.lowDate} — {fmtVal(data.summary?.lowValue || 0)}</Typography>} /></Grid>
        </Grid>

        {data.historicalData?.trends?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <strong>Trend:</strong> {data.historicalData.trends.direction} ({data.historicalData.trends.percentage >= 0 ? '+' : ''}{data.historicalData.trends.percentage}%) — {data.historicalData.trends.description}
          </Typography>
        )}

        {data.confidence?.factors?.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Confidence factors:</Typography>
            {data.confidence.factors.map((f: string, i: number) => (
              <Chip key={i} label={f} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5, ...getSoftChipSx('default', isDark) }} />
            ))}
          </Box>
        )}

        {chartData.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <ChartCard title={`Historical vs. Predicted ${forecastMetricLabel(data.forecastType)}`} cardBg={cardBg} height={320}>
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: axisColor }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={compactNum} width={48} />
                  <Tooltip formatter={(v: any) => (isCurrency ? `${sym} ${Number(v).toLocaleString()}` : Number(v).toLocaleString())} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="actual" name="Historical" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} connectNulls={false} />
                  <Line type="monotone" dataKey="predicted" name="Predicted" stroke={CHART_COLORS[2]} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} />
                  <Line type="monotone" dataKey="upperBound" name="Upper bound" stroke={CHART_COLORS[2]} strokeOpacity={0.35} strokeWidth={1} dot={false} connectNulls={false} />
                  <Line type="monotone" dataKey="lowerBound" name="Lower bound" stroke={CHART_COLORS[2]} strokeOpacity={0.35} strokeWidth={1} dot={false} connectNulls={false} />
                </LineChart>
              </ChartCard>
            </Grid>
          </Grid>
        )}

        <Typography variant="h6" gutterBottom>Day-by-Day Predictions</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Predicted</TableCell>
                <TableCell align="right">Lower Bound</TableCell>
                <TableCell align="right">Upper Bound</TableCell>
                <TableCell align="right">Confidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.predictions.map((p: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{p.date instanceof Date ? p.date.toLocaleDateString() : 'N/A'}</TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="medium">{fmtVal(p.predictedValue)}</Typography></TableCell>
                  <TableCell align="right">{fmtVal(p.lowerBound)}</TableCell>
                  <TableCell align="right">{fmtVal(p.upperBound)}</TableCell>
                  <TableCell align="right">{Math.round((p.confidence || 0) * (p.confidence <= 1 ? 100 : 1))}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // ── AI Insights (currently populated by the Forecast report only) ──────────
  const renderAIInsights = () => {
    const ai = report.aiInsights;
    if (!ai) return null;
    return (
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <Typography variant="h6">AI Insights &amp; Suggestions</Typography>
        </Stack>

        {ai.summary && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>{ai.summary}</Typography>
          </Paper>
        )}

        <Grid container spacing={2}>
          {[
            { title: 'Key Findings', items: ai.keyFindings || [], color: 'primary' },
            { title: 'Recommendations', items: ai.recommendations || [], color: 'success' },
            { title: 'Trends', items: ai.trends || [], color: 'info' },
            { title: 'Risk Factors', items: ai.riskFactors || [], color: 'error' },
            { title: 'Opportunities', items: ai.opportunities || [], color: 'secondary' },
          ].filter(section => section.items.length > 0).map((section, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: cardBg, backgroundImage: 'none', borderRadius: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>{section.title}</Typography>
                <Stack spacing={0.75}>
                  {section.items.map((item: string, i: number) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                      <Chip size="small" variant="outlined" label="" sx={{ width: 8, height: 8, minWidth: 8, mt: 0.7, ...getSoftChipSx(section.color, isDark) }} />
                      <Typography variant="body2" color="text.secondary">{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Generated by AI {ai.generatedAt instanceof Date ? `on ${ai.generatedAt.toLocaleString()}` : ''} — verify before acting on financial decisions.
        </Typography>
      </Box>
    );
  };

  // ── Router ─────────────────────────────────────────────────────────────────
  const renderReportData = () => {
    switch (report.reportType) {
      case 'sales':                 return renderSalesReport();
      case 'inventory':             return renderInventoryReport();
      case 'purchase_orders':       return renderPurchaseOrderReport();
      case 'low_stock':             return renderLowStockReport();
      case 'warehouse_performance': return renderWarehousePerformanceReport();
      case 'product_performance':   return renderProductPerformanceReport();
      case 'revenue':               return renderRevenueReport();
      case 'returns_cancellations': return renderReturnsCancellationsReport();
      case 'profit_loss':           return renderProfitLossReport();
      case 'cost':                  return renderCostReport();
      case 'payments':              return renderPaymentsReport();
      case 'forecast':              return renderForecastReport();
      default: return <Typography>Report type not supported</Typography>;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: dialogBg,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: dialogBg,
          backgroundImage: 'none',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{report.reportName}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          bgcolor: dialogBg,
          backgroundImage: 'none',
        }}
      >
        {/* Meta */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Report Type</Typography>
              {/* isDark passed so chip uses dark-mode palette */}
              <Chip
                label={report.reportType.replace(/_/g, ' ')}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5, ...getSoftChipSx('primary', isDark) }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Generated At</Typography>
              <Typography variant="body1" sx={{ color: 'text.primary' }}>
                {report.generatedAt?.toLocaleDateString()} {report.generatedAt?.toLocaleTimeString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Generated By</Typography>
              <Typography variant="body1" sx={{ color: 'text.primary' }}>{report.generatedByName}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Total Records</Typography>
              <Typography variant="body1" sx={{ color: 'text.primary' }}>{fmtNum(report.summary?.totalRecords || 0)}</Typography>
            </Grid>
            {report.description && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Description</Typography>
                <Typography variant="body1" sx={{ color: 'text.primary' }}>{report.description}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ mb: 2 }} />
        {renderReportData()}
        {renderAIInsights()}
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: dialogBg,
          backgroundImage: 'none',
        }}
      >
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleDownloadPDF} variant="contained" startIcon={<PdfIcon />}>Download PDF</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDetailsDialog;

// import React from 'react';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
//   Box,
//   Typography,
//   Grid,
//   Chip,
//   Divider,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   IconButton,
//   Stack,
//   LinearProgress,
// } from '@mui/material';
// import {
//   Close as CloseIcon,
//   PictureAsPdf as PdfIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
// } from '@mui/icons-material';
// import { Report } from './Report';
// import { ReportPDFGenerator } from './reportPDFGenerator';
// import { useCompany } from '../../../contexts/CompanyContext';
// import { getCurrencySymbol } from '../../../util/productUtils';

// interface ReportDetailsDialogProps {
//   open: boolean;
//   report: Report;
//   onClose: () => void;
// }

// const ReportDetailsDialog: React.FC<ReportDetailsDialogProps> = ({
//   open,
//   report,
//   onClose,
// }) => {
//   const { companyProfile } = useCompany();
//   const companyCurrency = companyProfile?.currency || 'LKR';
//   const currencySymbol = getCurrencySymbol(companyCurrency);

//   const handleDownloadPDF = async () => {
//     try {
//       await ReportPDFGenerator.generatePDF(report, companyCurrency);
//     } catch (error) {
//       console.error('Error downloading PDF:', error);
//       alert('Failed to download PDF');
//     }
//   };

//   const renderSalesReport = () => {
//     const data = report.data;
//     if (!data.orders) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Orders</Typography>
//               <Typography variant="h6">{data.summary.totalOrders}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Order Value</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.averageOrderValue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Tax</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalTax.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* Top Products */}
//         {data.summary.topProducts && data.summary.topProducts.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Products
//             </Typography>
//             <Grid container spacing={2}>
//               {data.summary.topProducts.slice(0, 5).map((product: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Typography variant="body2" fontWeight="medium" gutterBottom>
//                       {product.productName}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Quantity: {product.quantity}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Revenue: {currencySymbol} {product.revenue.toFixed(2)}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         <Typography variant="h6" gutterBottom>
//           Orders
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Order #</TableCell>
//                 <TableCell>Customer</TableCell>
//                 <TableCell>Date</TableCell>
//                 <TableCell align="right">Amount</TableCell>
//                 <TableCell>Payment</TableCell>
//                 <TableCell>Status</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.orders.slice(0, 50).map((order: any) => (
//                 <TableRow key={order.orderId}>
//                   <TableCell>{order.orderNumber}</TableCell>
//                   <TableCell>{order.customerName}</TableCell>
//                   <TableCell>
//                     {order.orderDate instanceof Date 
//                       ? order.orderDate.toLocaleDateString() 
//                       : 'N/A'}
//                   </TableCell>
//                   <TableCell align="right">{currencySymbol} {order.totalAmount.toFixed(2)}</TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={order.paymentMethod} 
//                       size="small" 
//                       variant="outlined"
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Chip label={order.status} size="small" />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderInventoryReport = () => {
//     const data = report.data;
//     if (!data.products) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Products</Typography>
//               <Typography variant="h6">{data.summary.totalProducts}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Stock</Typography>
//               <Typography variant="h6">{data.summary.totalStock}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Stock Value</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalValue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Low Stock</Typography>
//               <Typography variant="h6" color="warning.main">{data.summary.lowStockItems}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Products
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Product</TableCell>
//                 <TableCell>SKU</TableCell>
//                 <TableCell>Category</TableCell>
//                 <TableCell align="right">Stock</TableCell>
//                 <TableCell align="right">Value</TableCell>
//                 <TableCell>Status</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.products.slice(0, 50).map((product: any) => (
//                 <TableRow key={product.productId}>
//                   <TableCell>
//                     {product.productName}
//                     {product.hasVariants && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         {product.variants?.length || 0} variants
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>{product.sku}</TableCell>
//                   <TableCell>{product.category}</TableCell>
//                   <TableCell align="right">{product.totalStock}</TableCell>
//                   <TableCell align="right">{currencySymbol} {product.stockValue.toFixed(2)}</TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={product.status.replace('_', ' ')} 
//                       size="small"
//                       color={product.status === 'in_stock' ? 'success' : product.status === 'low_stock' ? 'warning' : 'error'}
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderPurchaseOrderReport = () => {
//     const data = report.data;
//     if (!data.purchaseOrders || data.purchaseOrders.length === 0) {
//       return (
//         <Box sx={{ py: 4, textAlign: 'center' }}>
//           <Typography variant="h6" color="text.secondary" gutterBottom>
//             No Purchase Orders Found
//           </Typography>
//           <Typography variant="body2" color="text.secondary">
//             No purchase orders match the selected criteria.
//           </Typography>
//         </Box>
//       );
//     }

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total POs</Typography>
//               <Typography variant="h6">{data.summary.totalPOs}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Amount</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalAmount?.toFixed(2) || '0.00'}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Paid</Typography>
//               <Typography variant="h6" color="success.main">
//                 {currencySymbol} {data.summary.totalPaid?.toFixed(2) || '0.00'}
//               </Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Pending</Typography>
//               <Typography variant="h6" color="warning.main">
//                 {currencySymbol} {((data.summary.totalAmount || 0) - (data.summary.totalPaid || 0)).toFixed(2)}
//               </Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* Status Breakdown */}
//         {data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Status Breakdown
//             </Typography>
//             <Grid container spacing={1}>
//               {Object.entries(data.summary.statusBreakdown).map(([status, count]) => (
//                 <Grid item key={status}>
//                   <Chip 
//                     label={`${status}: ${count}`}
//                     size="small"
//                     sx={{ mr: 1, mb: 1 }}
//                   />
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Payment Status Breakdown */}
//         {data.summary.paymentStatusBreakdown && Object.keys(data.summary.paymentStatusBreakdown).length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Payment Status
//             </Typography>
//             <Grid container spacing={1}>
//               {Object.entries(data.summary.paymentStatusBreakdown).map(([status, count]) => (
//                 <Grid item key={status}>
//                   <Chip 
//                     label={`${status}: ${count}`}
//                     size="small"
//                     color={status === 'paid' ? 'success' : status === 'partial' ? 'warning' : 'default'}
//                     sx={{ mr: 1, mb: 1 }}
//                   />
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Top Suppliers */}
//         {data.summary.topSuppliers && data.summary.topSuppliers.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Suppliers
//             </Typography>
//             <Grid container spacing={2}>
//               {data.summary.topSuppliers.slice(0, 5).map((supplier: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Typography variant="body2" fontWeight="medium" gutterBottom>
//                       {supplier.supplierName}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Orders: {supplier.poCount}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Total: {currencySymbol} {supplier.totalAmount.toFixed(2)}
//                     </Typography>
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         <Typography variant="h6" gutterBottom>
//           Purchase Orders
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>PO #</TableCell>
//                 <TableCell>Supplier</TableCell>
//                 <TableCell>Order Date</TableCell>
//                 <TableCell>Expected Delivery</TableCell>
//                 <TableCell align="right">Amount</TableCell>
//                 <TableCell>Status</TableCell>
//                 <TableCell>Payment</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.purchaseOrders.slice(0, 50).map((po: any) => (
//                 <TableRow key={po.poId}>
//                   <TableCell>
//                     <Typography variant="body2" fontWeight="medium">
//                       {po.poNumber}
//                     </Typography>
//                   </TableCell>
//                   <TableCell>
//                     <Typography variant="body2">{po.supplierName}</Typography>
//                   </TableCell>
//                   <TableCell>
//                     <Typography variant="body2">
//                       {po.orderDate instanceof Date 
//                         ? po.orderDate.toLocaleDateString() 
//                         : 'N/A'}
//                     </Typography>
//                   </TableCell>
//                   <TableCell>
//                     <Typography variant="body2">
//                       {po.expectedDelivery instanceof Date 
//                         ? po.expectedDelivery.toLocaleDateString() 
//                         : 'Not set'}
//                     </Typography>
//                   </TableCell>
//                   <TableCell align="right">
//                     <Typography variant="body2" fontWeight="medium">
//                       {currencySymbol} {po.totalAmount?.toFixed(2) || '0.00'}
//                     </Typography>
//                     {po.paidAmount > 0 && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         Paid: {currencySymbol} {po.paidAmount?.toFixed(2)}
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={po.status || 'draft'} 
//                       size="small"
//                       color={
//                         po.status === 'Received' ? 'success' :
//                         po.status === 'Approved' ? 'info' :
//                         po.status === 'Pending' ? 'warning' : 'default'
//                       }
//                     />
//                   </TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={po.paymentStatus || 'pending'} 
//                       size="small"
//                       color={
//                         po.paymentStatus === 'Paid' ? 'success' :
//                         po.paymentStatus === 'Partial' ? 'warning' : 'default'
//                       }
//                       variant="outlined"
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
        
//         {data.purchaseOrders.length > 50 && (
//           <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
//             Showing first 50 of {data.purchaseOrders.length} purchase orders
//           </Typography>
//         )}
//       </Box>
//     );
//   };

//   const renderLowStockReport = () => {
//     const data = report.data;
//     if (!data.items) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Low Stock Items</Typography>
//               <Typography variant="h6" color="warning.main">{data.summary.totalLowStockItems}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Out of Stock</Typography>
//               <Typography variant="h6" color="error.main">{data.summary.totalOutOfStockItems}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Affected Products</Typography>
//               <Typography variant="h6">{data.summary.affectedProducts}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Affected Warehouses</Typography>
//               <Typography variant="h6">{data.summary.affectedWarehouses}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Low Stock Items
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Product</TableCell>
//                 <TableCell>Warehouse</TableCell>
//                 <TableCell align="right">Current</TableCell>
//                 <TableCell align="right">Min</TableCell>
//                 <TableCell align="right">Deficit</TableCell>
//                 <TableCell>Status</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.items.map((item: any, index: number) => (
//                 <TableRow key={index}>
//                   <TableCell>
//                     {item.productName}
//                     {item.variantName && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         {item.variantName}
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>{item.warehouseName}</TableCell>
//                   <TableCell align="right">{item.currentStock}</TableCell>
//                   <TableCell align="right">{item.minStock}</TableCell>
//                   <TableCell align="right">{item.deficit}</TableCell>
//                   <TableCell>
//                     <Chip 
//                       label={item.status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock'} 
//                       size="small"
//                       color={item.status === 'out_of_stock' ? 'error' : 'warning'}
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderWarehousePerformanceReport = () => {
//     const data = report.data;
//     if (!data.warehouses) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Warehouses</Typography>
//               <Typography variant="h6">{data.summary.totalWarehouses}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Utilization</Typography>
//               <Typography variant="h6">{data.summary.averageUtilization}%</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Most Utilized</Typography>
//               <Typography variant="body2">{data.summary.mostUtilizedWarehouse}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Least Utilized</Typography>
//               <Typography variant="body2">{data.summary.leastUtilizedWarehouse}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Warehouses
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Warehouse</TableCell>
//                 <TableCell>Code</TableCell>
//                 <TableCell align="right">Products</TableCell>
//                 <TableCell align="right">Stock</TableCell>
//                 <TableCell align="right">Capacity Used</TableCell>
//                 <TableCell align="right">Utilization</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.warehouses.map((warehouse: any) => (
//                 <TableRow key={warehouse.warehouseId}>
//                   <TableCell>
//                     {warehouse.warehouseName}
//                     {warehouse.manager && (
//                       <Typography variant="caption" color="text.secondary" display="block">
//                         Manager: {warehouse.manager}
//                       </Typography>
//                     )}
//                   </TableCell>
//                   <TableCell>{warehouse.code}</TableCell>
//                   <TableCell align="right">{warehouse.totalProducts}</TableCell>
//                   <TableCell align="right">{warehouse.totalStock}</TableCell>
//                   <TableCell align="right">{warehouse.capacityUsed}/{warehouse.capacityTotal}</TableCell>
//                   <TableCell align="right">
//                     <Chip 
//                       label={`${warehouse.utilizationRate}%`}
//                       size="small"
//                       color={warehouse.utilizationRate > 80 ? 'error' : warehouse.utilizationRate > 60 ? 'warning' : 'success'}
//                     />
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderProductPerformanceReport = () => {
//     const data = report.data;
//     if (!data.products) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Products</Typography>
//               <Typography variant="h6">{data.summary.totalProducts}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Best Seller</Typography>
//               <Typography variant="body2">{data.summary.bestSellingProduct}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Turnover</Typography>
//               <Typography variant="h6">{data.summary.averageStockTurnover}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         <Typography variant="h6" gutterBottom>
//           Products
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Product</TableCell>
//                 <TableCell>Category</TableCell>
//                 <TableCell align="right">Sold</TableCell>
//                 <TableCell align="right">Revenue</TableCell>
//                 <TableCell align="right">Avg Price</TableCell>
//                 <TableCell align="right">Turnover</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.products.slice(0, 50).map((product: any) => (
//                 <TableRow key={product.productId}>
//                   <TableCell>
//                     {product.productName}
//                     {product.isFeatured && (
//                       <Chip label="Featured" size="small" color="primary" sx={{ ml: 1 }} />
//                     )}
//                   </TableCell>
//                   <TableCell>{product.category}</TableCell>
//                   <TableCell align="right">{product.totalSold}</TableCell>
//                   <TableCell align="right">{currencySymbol} {product.revenue.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol} {product.averagePrice.toFixed(2)}</TableCell>
//                   <TableCell align="right">{product.stockTurnover}</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderRevenueReport = () => {
//     const data = report.data;
//     if (!data.dailyRevenue) return null;

//     return (
//       <Box>
//         <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
//           Summary
//         </Typography>
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Net Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalNetRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Orders</Typography>
//               <Typography variant="h6">{data.summary.totalOrders}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Order Value</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.averageOrderValue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Avg Daily Revenue</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.averageDailyRevenue.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Growth Rate</Typography>
//               <Stack direction="row" alignItems="center" spacing={0.5}>
//                 <Typography variant="h6" color={data.summary.growthRate >= 0 ? 'success.main' : 'error.main'}>
//                   {data.summary.growthRate >= 0 ? '+' : ''}{data.summary.growthRate.toFixed(2)}%
//                 </Typography>
//                 {data.summary.growthRate >= 0 ? (
//                   <TrendingUpIcon color="success" fontSize="small" />
//                 ) : (
//                   <TrendingDownIcon color="error" fontSize="small" />
//                 )}
//               </Stack>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Highest Revenue Day</Typography>
//               <Typography variant="body2">{data.summary.highestRevenueDay}</Typography>
//             </Paper>
//           </Grid>
//           <Grid item xs={6} sm={4} md={3}>
//             <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
//               <Typography variant="caption" color="text.secondary">Total Discount</Typography>
//               <Typography variant="h6">{currencySymbol} {data.summary.totalDiscount.toFixed(2)}</Typography>
//             </Paper>
//           </Grid>
//         </Grid>

//         {/* Top Products */}
//         {data.topProducts && data.topProducts.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Products by Revenue
//             </Typography>
//             <Grid container spacing={2}>
//               {data.topProducts.slice(0, 5).map((product: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
//                       <Typography variant="body2" fontWeight="medium">
//                         {product.productName}
//                       </Typography>
//                       <Chip 
//                         label={`${product.percentageOfTotal.toFixed(1)}%`}
//                         size="small"
//                         color="primary"
//                       />
//                     </Stack>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Revenue: {currencySymbol} {product.revenue.toFixed(2)}
//                     </Typography>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Units Sold: {product.totalSold}
//                     </Typography>
//                     <LinearProgress 
//                       variant="determinate" 
//                       value={product.percentageOfTotal} 
//                       sx={{ mt: 1, height: 6, borderRadius: 1 }}
//                     />
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Top Categories */}
//         {data.topCategories && data.topCategories.length > 0 && (
//           <Box sx={{ mb: 3 }}>
//             <Typography variant="subtitle1" gutterBottom>
//               Top Categories by Revenue
//             </Typography>
//             <Grid container spacing={2}>
//               {data.topCategories.slice(0, 5).map((category: any, index: number) => (
//                 <Grid item xs={12} sm={6} md={4} key={index}>
//                   <Paper sx={{ p: 2 }}>
//                     <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
//                       <Typography variant="body2" fontWeight="medium">
//                         {category.categoryName}
//                       </Typography>
//                       <Chip 
//                         label={`${category.percentageOfTotal.toFixed(1)}%`}
//                         size="small"
//                         color="secondary"
//                       />
//                     </Stack>
//                     <Typography variant="caption" color="text.secondary" display="block">
//                       Revenue: {currencySymbol} {category.revenue.toFixed(2)}
//                     </Typography>
//                     <LinearProgress 
//                       variant="determinate" 
//                       value={category.percentageOfTotal} 
//                       color="secondary"
//                       sx={{ mt: 1, height: 6, borderRadius: 1 }}
//                     />
//                   </Paper>
//                 </Grid>
//               ))}
//             </Grid>
//           </Box>
//         )}

//         {/* Daily Revenue Table */}
//         <Typography variant="h6" gutterBottom>
//           Daily Revenue
//         </Typography>
//         <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
//           <Table size="small" stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell>Date</TableCell>
//                 <TableCell align="right">Orders</TableCell>
//                 <TableCell align="right">Revenue</TableCell>
//                 <TableCell align="right">Net Revenue</TableCell>
//                 <TableCell align="right">Avg Order</TableCell>
//                 <TableCell align="right">Tax</TableCell>
//                 <TableCell align="right">Shipping</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {data.dailyRevenue.map((day: any, index: number) => (
//                 <TableRow key={index}>
//                   <TableCell>
//                     {day.date instanceof Date 
//                       ? day.date.toLocaleDateString() 
//                       : 'N/A'}
//                   </TableCell>
//                   <TableCell align="right">{day.totalOrders}</TableCell>
//                   <TableCell align="right">
//                     <Typography variant="body2" fontWeight="medium">
//                       {currencySymbol} {day.totalRevenue.toFixed(2)}
//                     </Typography>
//                   </TableCell>
//                   <TableCell align="right">{currencySymbol} {day.netRevenue.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol} {day.averageOrderValue.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol} {day.totalTax.toFixed(2)}</TableCell>
//                   <TableCell align="right">{currencySymbol}{day.totalShipping.toFixed(2)}</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Box>
//     );
//   };

//   const renderReportData = () => {
//     switch (report.reportType) {
//       case 'sales':
//         return renderSalesReport();
//       case 'inventory':
//         return renderInventoryReport();
//       case 'purchase_orders':
//         return renderPurchaseOrderReport();
//       case 'low_stock':
//         return renderLowStockReport();
//       case 'warehouse_performance':
//         return renderWarehousePerformanceReport();
//       case 'product_performance':
//         return renderProductPerformanceReport();
//       case 'revenue':
//         return renderRevenueReport();
//       default:
//         return <Typography>Report type not supported</Typography>;
//     }
//   };

//   return (
//     <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
//       <DialogTitle>
//         <Stack direction="row" justifyContent="space-between" alignItems="center">
//           <Typography variant="h6">{report.reportName}</Typography>
//           <IconButton onClick={onClose} size="small">
//             <CloseIcon />
//           </IconButton>
//         </Stack>
//       </DialogTitle>
//       <DialogContent dividers>
//         <Box sx={{ mb: 3 }}>
//           <Grid container spacing={2}>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Report Type</Typography>
//               <Chip label={report.reportType.replace('_', ' ')} color="primary" size="small" sx={{ mt: 0.5 }} />
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Generated At</Typography>
//               <Typography variant="body1">
//                 {report.generatedAt?.toLocaleDateString()} {report.generatedAt?.toLocaleTimeString()}
//               </Typography>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Generated By</Typography>
//               <Typography variant="body1">{report.generatedByName}</Typography>
//             </Grid>
//             <Grid item xs={12} sm={6}>
//               <Typography variant="body2" color="text.secondary">Total Records</Typography>
//               <Typography variant="body1">{report.summary?.totalRecords || 0}</Typography>
//             </Grid>
//             {report.description && (
//               <Grid item xs={12}>
//                 <Typography variant="body2" color="text.secondary">Description</Typography>
//                 <Typography variant="body1">{report.description}</Typography>
//               </Grid>
//             )}
//           </Grid>
//         </Box>

//         <Divider sx={{ mb: 2 }} />

//         {renderReportData()}
//       </DialogContent>
//       <DialogActions>
//         <Button onClick={onClose}>Close</Button>
//         <Button
//           onClick={handleDownloadPDF}
//           variant="contained"
//           startIcon={<PdfIcon />}
//         >
//           Download PDF
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default ReportDetailsDialog;